import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { resolveTier } from '../../hooks/useTier';
import { CompanySwitcher } from './CompanySwitcher';
import { useThemeContext } from '../../contexts/ThemeContext';
import { Button } from '../ui/button';

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'th', label: 'TH' },
  { code: 'zh', label: 'ZH' },
];

// Path segment -> i18n nav key, for breadcrumb + page title.
const SEGMENT_LABELS: Record<string, string> = {
  dashboard: 'nav.dashboard',
  companies: 'nav.companies',
  sellers: 'nav.sellers',
  users: 'nav.users',
  'api-config': 'nav.apiConfig',
  sync: 'nav.sync',
  'sub-sellers': 'nav.subSellers',
  merchants: 'nav.merchants',
  stats: 'nav.stats',
  payouts: 'nav.payouts',
};

function SunIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="6" />
      <line x1="12" y1="18" x2="12" y2="22" />
      <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
      <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
      <line x1="2" y1="12" x2="6" y2="12" />
      <line x1="18" y1="12" x2="22" y2="12" />
      <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
      <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" />
    </svg>
  );
}

export function TopBar({ onMenuClick }: { onMenuClick?: () => void }) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useThemeContext();
  const { pathname } = useLocation();
  const tier = resolveTier(user);

  // segments after the role prefix, e.g. /admin/sellers/:id -> ['sellers', ':id']
  const segments = pathname.split('/').filter(Boolean);
  const sectionSeg = segments[1];
  const titleKey = sectionSeg ? SEGMENT_LABELS[sectionSeg] : 'nav.dashboard';
  const title = titleKey ? t(titleKey) : (sectionSeg ?? '');

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 sm:gap-4">
      {/* Hamburger (mobile) + breadcrumb + page title */}
      <div className="flex min-w-0 items-center gap-3">
        <button
          onClick={onMenuClick}
          aria-label={t('nav.menu')}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border border-white/25 text-white/90 transition-colors hover:bg-white/15 lg:hidden"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <div className="min-w-0">
          <nav className="flex items-center gap-1.5 text-xs text-white/70">
            <span aria-hidden>⌂</span>
            <span>/</span>
            <span className="truncate font-medium text-white/90">{title}</span>
          </nav>
          <h1 className="mt-1 truncate text-lg font-bold tracking-tight text-white sm:text-xl">
            {title}
          </h1>
        </div>
      </div>

      {/* Action cluster */}
      <div className="flex items-center gap-2.5">
        {tier === 'super' && <CompanySwitcher />}

        <button
          onClick={toggleTheme}
          title={isDark ? t('common.lightMode') : t('common.darkMode')}
          aria-label={isDark ? t('common.lightMode') : t('common.darkMode')}
          className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/25 text-white/90 transition-colors hover:bg-white/15"
        >
          {isDark ? <SunIcon /> : <MoonIcon />}
        </button>

        <div className="flex overflow-hidden rounded-xl border border-white/25">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => i18n.changeLanguage(lang.code)}
              className={`px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                i18n.language === lang.code
                  ? 'bg-white text-primary-700'
                  : 'text-white/80 hover:bg-white/15'
              }`}
            >
              {lang.label}
            </button>
          ))}
        </div>

        {user && (
          <div className="hidden text-right sm:block">
            <div className="text-sm font-semibold leading-tight text-white">{user.displayName}</div>
            <div className="text-[0.7rem] leading-tight text-white/70">
              {tier ? t(`adminTier.${tier}`) : t(`roles.${user.role}`)}
            </div>
          </div>
        )}

        <Button
          variant="ghost"
          size="sm"
          onClick={logout}
          className="border border-white/25 text-white hover:bg-white/15"
        >
          {t('auth.logout')}
        </Button>
      </div>
    </div>
  );
}
